import React from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Link as MuiLink,
    Typography,
    Paper,
    Box as MuiBox,
    Tooltip,
    TableSortLabel,
    Chip,
    useTheme 
} from '@mui/material';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'; 
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined'; // For phone icon

const CandidateResultsTable = ({
    sortedCandidates,
    sortConfig,
    handleSortRequest,
    headCells,
    getScoreChipColor, 
    getScoreColorForText 
}) => {
    const theme = useTheme(); 

    const formatPhoneNumber = (str) =>
        /^\+1\d{10}$/.test(str)
            ? str.replace(/^(\+1)(\d{3})(\d{3})(\d{4})$/, "$1 ($2) $3-$4")
            : str;

    return (
        <Paper
            variant="outlined"
            sx={{
                mt: 1,
                width: '100%',
                borderRadius: '12px',
                borderColor: theme.palette.divider, 
                overflow: 'hidden', 
                backgroundColor: theme.palette.background.paper, 
            }}
        >
            <MuiBox sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
                <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                    Top 2% Candidates
                </Typography>
            </MuiBox>
            {/* TableContainer */}
            <TableContainer sx={{ maxHeight: { xs: 700, sm: 800, md: 900 } }}>
                <Table stickyHeader aria-label="sleek candidates table">
                    <TableHead>
                        <TableRow>
                            {headCells.map((headCell) => (
                                <TableCell
                                    key={headCell.id}
                                    align={headCell.numeric ? 'right' : 'left'}
                                    padding={headCell.disablePadding ? 'none' : 'normal'}
                                    sortDirection={sortConfig.key === headCell.id ? sortConfig.direction : false}
                                    sx={{
                                        backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100],
                                        color: theme.palette.text.secondary,
                                        fontWeight: 500,
                                        fontSize: '0.8rem',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        borderBottom: `1px solid ${theme.palette.divider}`,
                                        py: 1,
                                        minWidth: headCell.minWidth,
                                    }}
                                >
                                    <MuiBox sx={{ display: 'flex', alignItems: 'center', justifyContent: headCell.numeric ? 'flex-end' : 'flex-start' }}>
                                        {headCell.sortable ? (
                                            <TableSortLabel
                                                active={sortConfig.key === headCell.id}
                                                direction={sortConfig.key === headCell.id ? sortConfig.direction : 'asc'}
                                                onClick={() => handleSortRequest(headCell.id)}
                                                sx={{ '& .MuiTableSortLabel-icon': { opacity: 0.8, color: sortConfig.key === headCell.id ? theme.palette.primary.main : 'inherit' } }}
                                            >
                                                {headCell.label}
                                            </TableSortLabel>
                                        ) : (
                                            headCell.label
                                        )}
                                        {headCell.id === 'similarity_score' && (
                                            <Tooltip title="Measures how closely the candidate's profile matches the job description keywords and criteria." arrow placement="top">
                                                <InfoOutlinedIcon sx={{ fontSize: '0.9rem', ml: 0.5, color: theme.palette.action.active, cursor: 'help' }} />
                                            </Tooltip>
                                        )}
                                        {headCell.id === 'skill_score' && (
                                            <Tooltip title="Represents the candidate's assessed level of competence in relevant skills." arrow placement="top">
                                                <InfoOutlinedIcon sx={{ fontSize: '0.9rem', ml: 0.5, color: theme.palette.action.active, cursor: 'help' }} />
                                            </Tooltip>
                                        )}
                                         {/* Tooltip for Final Score Header */}
                                         {headCell.id === 'final_score' && (
                                            <Tooltip title="Overall score combining similarity to job description and assessed skill competence. Higher is better." arrow placement="top">
                                                <InfoOutlinedIcon sx={{ fontSize: '0.9rem', ml: 0.5, color: theme.palette.action.active, cursor: 'help' }} />
                                            </Tooltip>
                                        )}
                                    </MuiBox>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sortedCandidates.map((candidate, index) => { 
                            const fullName = candidate.name || 'N/A';
                            const candidateEmail = candidate.email || 'N/A';
                            const candidatePhone = candidate.phone;
                            const finalScoreDisplay = typeof candidate.final_score === 'number' ? candidate.final_score.toFixed(3) : 'N/A';
                            const finalScoreTextColor = getScoreColorForText(candidate.final_score, theme);
                            const skillScoreChipColorName = getScoreChipColor(candidate.skill_score); 

                            return (
                                <TableRow
                                    hover
                                    key={`candidate-row-${candidate.uuid || candidate.id || 'no_id'}-${index}`}
                                    sx={{
                                        '&:last-child td, &:last-child th': { border: 0 },
                                        '&:hover': { backgroundColor: theme.palette.action.hover },
                                        height: '64px', 
                                    }}
                                >
                                    <TableCell sx={{ py: 1, fontSize: '0.875rem', fontWeight: 500, color: theme.palette.text.primary, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {fullName}
                                    </TableCell>
                                    <TableCell sx={{ py: 1, px: 2, fontSize: '0.875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {candidateEmail === 'N/A' ? 'N/A' : (
                                            <MuiLink href={`mailto:${candidateEmail}`} underline="none" sx={{ color: theme.palette.primary.main, display: 'flex', alignItems: 'center', '&:hover': { textDecoration: 'underline' } }}>
                                                <EmailOutlinedIcon sx={{ fontSize: '1rem', mr: 0.75, color: theme.palette.text.secondary }} />
                                                {candidateEmail}
                                            </MuiLink>
                                        )}
                                    </TableCell>
                                    <TableCell sx={{ py: 1, px: 2, fontSize: '0.875rem', color: theme.palette.text.secondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {candidatePhone && candidatePhone !== 'None' && candidatePhone !== 'N/A' ? (
                                            <MuiBox sx={{ display: 'flex', alignItems: 'center' }}>
                                                <PhoneOutlinedIcon sx={{ fontSize: '1rem', mr: 0.75 }} />
                                                {formatPhoneNumber(candidatePhone)}
                                            </MuiBox>
                                        ) : (
                                            'N/A'
                                        )}
                                    </TableCell>
                                    <TableCell align="right" sx={{ py: 1, fontSize: '0.875rem', color: theme.palette.text.secondary }}>
                                        {typeof candidate.similarity_score === 'number' ? candidate.similarity_score.toFixed(3) : 'N/A'}
                                    </TableCell>
                                    <TableCell align="right" sx={{ py: 1}}>
                                        {typeof candidate.skill_score === 'number' ? (
                                            <Chip
                                                label={candidate.skill_score.toFixed(2)}
                                                size="small"
                                                variant="outlined"
                                                color={skillScoreChipColorName} 
                                                sx={{ height: '24px', fontSize: '0.75rem', fontWeight: 500, borderRadius: '8px' }}
                                            />
                                        ) : <Typography variant="caption" color="textSecondary">N/A</Typography>}
                                    </TableCell>
                                    <TableCell align="right" sx={{ py: 1, fontSize: '0.9rem', fontWeight: 600, color: finalScoreTextColor }}>
                                        <Tooltip
                                            arrow
                                            placement="top"
                                            title={
                                                <MuiBox sx={{ p: 1 }}>
                                                    <Typography variant="caption" display="block" sx={{ fontWeight: 'bold', mb: 0.5 }}>Score Details:</Typography>
                                                    <Typography variant="caption" display="block">Similarity: {typeof candidate.similarity_score === 'number' ? candidate.similarity_score.toFixed(3) : 'N/A'}</Typography>
                                                    <Typography variant="caption" display="block">Skill: {typeof candidate.skill_score === 'number' ? candidate.skill_score.toFixed(2) : 'N/A'}</Typography>
                                                </MuiBox>
                                            }
                                        >
                                            <span>{finalScoreDisplay}</span>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
};

export default CandidateResultsTable;
